import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Study & Learning — the owner's ACTIVE learning of subjects, with a mastery
 * progression and logged practice sessions.
 *
 * This is distinct from the Knowledge graph (concepts/notes and their typed
 * relationships). A StudySubject is "I am learning X right now"; mastery is a
 * personal state, not a property of the concept itself (per the learning-model
 * doc). Sessions are lightweight practice evidence — never a guilt log.
 */

/** Personal mastery state, ordered from unknown to teachable. */
export type MasteryState = 'unknown' | 'aware' | 'learning' | 'practicing' | 'usable' | 'strong' | 'teachable';

export const MASTERY_STATES: readonly MasteryState[] = [
  'unknown',
  'aware',
  'learning',
  'practicing',
  'usable',
  'strong',
  'teachable',
] as const;

/** Numeric rank of a mastery state (0..6) for progress display. Pure. */
export function masteryRank(state: MasteryState): number {
  return MASTERY_STATES.indexOf(state);
}

/** Fraction 0..1 of progress toward "teachable". Pure. */
export function masteryFraction(state: MasteryState): number {
  return masteryRank(state) / (MASTERY_STATES.length - 1);
}

export type SubjectStatus = 'active' | 'paused' | 'done';

export interface StudySubject extends RecordEnvelope {
  readonly kind: 'study_subject';
  readonly title: string;
  readonly masteryState: MasteryState;
  readonly status: SubjectStatus;
  /** Linked goals/skills/concepts/projects (ids only, no duplication). */
  readonly linkedIds: readonly EntityId[];
}

export interface StudySubjectInput {
  readonly title: string;
  readonly masteryState?: MasteryState;
  readonly privacyClass?: PrivacyClass;
}

export interface StudySession extends RecordEnvelope {
  readonly kind: 'study_session';
  readonly subjectId: EntityId;
  readonly minutes: number;
  /** ISO date the session is counted under. */
  readonly occurredOn: string;
  readonly note: string;
}

export interface StudySessionInput {
  readonly subjectId: EntityId;
  readonly minutes: number;
  readonly occurredOn?: string;
  readonly note?: string;
  readonly privacyClass?: PrivacyClass;
}

export interface StudySummary {
  readonly totalMinutes: number;
  readonly sessionCount: number;
  readonly daysStudied: number;
  readonly minutesBySubject: Record<string, number>;
}

const dayOf = (iso: string): string => iso.slice(0, 10);

/** Total practice minutes and breakdown over [fromIso, toIso] (inclusive). Pure. */
export function summarizeStudy(
  sessions: readonly Pick<StudySession, 'subjectId' | 'minutes' | 'occurredOn'>[],
  fromIso: string,
  toIso: string,
): StudySummary {
  const from = dayOf(fromIso);
  const to = dayOf(toIso);
  const inRange = sessions.filter((s) => {
    const d = dayOf(s.occurredOn);
    return d >= from && d <= to;
  });
  const minutesBySubject: Record<string, number> = {};
  const days = new Set<string>();
  let totalMinutes = 0;
  for (const s of inRange) {
    const m = Math.max(0, Math.trunc(s.minutes));
    totalMinutes += m;
    const key = String(s.subjectId);
    minutesBySubject[key] = (minutesBySubject[key] ?? 0) + m;
    days.add(dayOf(s.occurredOn));
  }
  return { totalMinutes, sessionCount: inRange.length, daysStudied: days.size, minutesBySubject };
}
